// FLAGS: none
// Shared constant values: two-factor method, selectable languages, questionnaire identifiers, page sizes.
// USER_AVAILABLE_LANGUAGES offers five languages while i18n.js loads resources for only en and nl, so the three
// others fall back rather than translate.
import { ModelsTwoFactorAuthMethod } from "@src/api/v1/data-contracts";

export const AuthenticatorTwoFactorCodeRequestMethod =
  ModelsTwoFactorAuthMethod.AuthenticatorKey;

export const USER_AVAILABLE_LANGUAGES = [
  { label: "EN", value: "en-GB" },
  { label: "NL", value: "nl-NL" },
  { label: "DE", value: "de-DE" },
  { label: "FR", value: "fr-FR" },
  { label: "ES", value: "es-ES" },
];

export const GenderIdentifier = "Gender";
export const GENDER = {
  MALE: "male",
  FEMALE: "female",
  UNKNOWN: "unknown",
};

export const AVAILABLE_QUESTIONNAIRES = [{ label: "SQ48", value: "SQ48" }];

export const apiPaths = {
  getTwoFactorRegistration: "v1/users/2fa/authenticator/registration-info",
  enableTwoFactorLogin: "v1/users/2fa/authenticator/enable",
  verifyTwoFactorCode: "v1/users/2fa/validate",
  grantCalendarAccess: "v1/google-calendar/grant-access",
  createChat: "v1/chat/channel",

  getSessionType: "v1/tags/SessionType",
  getSessionLanguage: "v1/tags/SessionLanguage",

  profile: "v2/users/profile",
  login: "v2/users/login",
  logout: "v2/users/logout",
  refreshToken: "v2/users/refresh-token",

  externalLogin: "v2/external-login/login-code-flow",
  externalSignUp: "v2/external-login/register-code-flow",

  providerChallenge: "v1/external-login/provider-challenge",
  reschedule: "v1/reschedule", // this is meant for `${scheduler}/consult/${consultId}/reschedule`
  videoCalling: "v1/video-calling",
  spaces: "v1/spaces",
  hostedSpaces: "v1/spaces/hosted",
  psychologists: "v1/psychologists",
  psychologistManagement: "v1/psychologist-management",
  reassignPsychologist: "v1/psychologist-management/reassign-psychologist",
  makePsychologist: "v1/psychologist-management/make-psychologist",
  openupEmployees: "v1/psychologist-management/openup-employees",
  psychologistThemes: "v1/psychologists/themes",
  setPsychologistTheme: "v1/psychologist-management/set-psychologist-themes",
  psychologistConsults: "v1/psychologists/consults",
  psychologistAllConsults: "v1/consults/all-sessions",
  psychologistProfile: "v1/psychologists/profile",
  expertDetails: "v1/expert-details",
  clients: "v1/clients",
  myClients: "v1/clients/my",
  getGoogleCalendar: "v1/google-calendar/get-calendars",
  registerGoogleCalendar: "v1/google-calendar/register-calendar",
  employers: "v1/employers",
  consults: "v1/consults",
  clientConsults: "v1/client-consults",
  notifications: "v1/notifications",
  availableTimeSlots: "v1/scheduler/time-slots/available",
  scheduler: "v1/scheduler",
  timezones: "v1/scheduler/timezones",
  psychologistsTags: "v1/psychologists/tags",
  tags: "v1/tags",
  themes: "v1/themes",
  updateLanguage: "v1/users/update-language",
  myFeedbacks: "v1/psychologists/my-feedbacks",
  resyncCalendar: "v1/scheduler/resynchronize-calendar",
  sessionNoteTopicsByTheme: "v1/tags/topics-by-theme",
};

export const apiPathsWithParams = {
  getVideoCallToken: (roomId: string) =>
    `${apiPaths.videoCalling}/token?roomId=${roomId}`,
  uploadChatFile: (chatThreadId: string) =>
    `${apiPaths.videoCalling}/chat/${chatThreadId}/attachments`,
  getClientSpaces: (clientId: string) =>
    `${apiPaths.spaces}/client/${clientId}`,
  whoElseIsInCall: (roomId: string) =>
    `${apiPaths.videoCalling}/who-else-in-call?roomId=${roomId}`,
  getEmployerReferralToolkits: (employerId: string) =>
    `${apiPaths.employers}/${employerId}/referral-toolkit`,
  downloadEmployerRefferalToolkit: (employerId: string, fileName: string) =>
    `api/${apiPaths.employers}/${employerId}/referral-toolkit/files?fileName=${fileName}`,
  resyncCalendar: (calendarId: string) =>
    `${apiPaths.resyncCalendar}/${calendarId}`,
};

export const STATUS = {
  SCHEDULED: "Scheduled",
  CANCELED_BY_CLIENT: "CancelledByClient",
  CANCELED_BY_OPENUP: "CancelledByOpenUp",
  COMPLETED: "Completed",
  MISSED_BY_CLIENT: "MissedByClient",
  MISSED_BY_OPENUP: "MissedByOpenUp",
  MISSED_BY_Both: "MissedByBoth",
};
export const STATUS_SUBJECT_FOR_CHANGE = [
  STATUS.MISSED_BY_Both,
  STATUS.MISSED_BY_CLIENT,
  STATUS.MISSED_BY_OPENUP,
  STATUS.COMPLETED,
  STATUS.CANCELED_BY_CLIENT,
  STATUS.CANCELED_BY_OPENUP,
];

export const TIMEZONES = {
  AMSTERDAM: "Europe/Amsterdam",
  BERLIN: "Europe/Berlin",
  KIEV: "Europe/Kiev",
  LONDON: "Europe/London",
  MOSCOW: "Europe/Moscow",
  PARIS: "Europe/Paris",
  PRAGUE: "Europe/Prague",
  RIYADH: "Asia/Riyadh",
  NEW_YORK: "America/New_York",
};

export const AmsterdamTimezone = "Europe/Amsterdam";

export const RESPONSE_STATUS = {
  OK: 200,
  BAD_REQUEST: 400,
  INTERNAL_SERVER_ERROR: 500,
  NOT_FOUND: 404,
};

export const TAG_TYPES = {
  SESSION_LANGUAGE: "SessionLanguage",
  SESSION_TYPE: "SessionType",
  USER_STATUS: "Status",
  USER_MINDSET: "Mindset",
  PSYCHOLOGIST_LANGUAGE: "PsychologistLanguage",
  PSYCHOLOGIST_STATUS: "PsychologistStatus",
  PSYCHOLOGIST_THEME: "PsychologistTheme",
  GENDER: "Gender",
  TOPICS: "Topics",
};

export const STATUS_TYPE = {
  INACTIVE: "inactive",
  ACTIVE_NOT_ACCEPTING: "active_not_accepting",
  ACTIVE_ACCEPTING: "active_accepting",
};

export const SESSION_TYPE = {
  GENERAL: "general",
  MINDFULNESS: "mindfulness",
  MEDICAL: "medical",
  PHYSICAL_WELLBEING: "physical-wellbeing",
  FINANCIAL_WELLBEING: "financial-wellbeing",
  CONFIDENTIAL_ADVISOR: "confidential-advisor",
};

export const TOPIC_NAMES = {
  CONFIDENTIAL_ADVISOR_OTHER: "ConfidentialAdvisorOther",
};

export const LANGUAGE_TYPES = {
  ARABIC: "ar-AR",
  MOROCCAN: "ar-MA",
  BOSNIAN: "bs-BA",
  GERMAN: "de-DE",
  GERMAN_AT: "de-AT",
  GERMAN_CH: "de-CH",
  ENGLISH_US: "en-US",
  ENGLISH_GB: "en-GB",
  SPANISH: "es-ES",
  FRENCH: "fr-FR",
  CROATIAN: "hr-HR",
  ITALIAN: "it-IT",
  DUTCH_BE: "nl-BE",
  FLEMISH: "vls-BE",
  DUTCH: "nl-NL",
  CHINESE: "zh-CN",
  SERBIAN: "sr-RS",
  RUSSIAN: "ru-RU",
  TURKISH: "tr-TR",
  PORTUGUESE: "pt-PT",
  UKRAINIAN: "uk-UA",
  POLISH: "pl-PL",
  BULGARIAN: "bg-BG",
  DANISH: "da-DK",
  SWEDISH: "sv-SE",
  FINNISH: "fi-FI",
  SOMALI: "so-SO",
  NORWEGIAN: "nb-NO",
  HUNGARIAN: "hu-HU",
  SLOVAK: "sk-SK",
  GREEK: "el-GR",
  EGYPTIAN: "ar-EG",
  TUNISIAN: "ar-TN",
  CZECH: "cs-CZ",
  SLOVENIAN: "sl-SL",
  AFRIKAANS: "af-ZA",
  MAURITIAN: "mfe-MU",
  JAPANESE: "ja-JP",
  INDONESIAN: "id-ID",
  KOREAN: "ko-KR",
  ROMANIAN: "ro-RO",
  HINDI: "hi-IN",
  ESTONIAN: "et-EE",
  WALLOON: "wa-BE",
  FRENCH_BE: "fr-BE",
  FRISIAN: "fy-NL",
  CANTONESE: "yue-HK",
  LITHUANIAN: "lt-LT",
  FARSI: "fa-IR",
  HEBREW: "he-IL",
};

export const THEME_KEY = {
  CONFIDENCE: "Confidence",
  STRESS: "Stress",
  WORK: "Work",
  PURPOSE: "Purpose",
  RELATIONSHIPS: "Relationships",
  HEALTH: "Health",
  SLEEP: "Sleep",
  MINDFULNESS: "Mindfulness",
  BIOMETRICS: "Biometrics",
  MENTAL_HEALTH: "MentalHealth",
  PERSONAL_INFORMATION: "PersonalInformation",
  MEDICAL_HISTORY: "MedicalHistory",
  INTOXICATIONS: "Intoxications",
  NUTRITION: "Nutrition",
  PHYSICAL_ACTIVITY: "PhysicalActivity",
  SLEEP_SATISFACTION: "SleepSatisfaction",
  BREATHING: "Breathing",
  DAILY_ENERGY: "DailyEnergy",
  MOTIVATION: "Motivation",
  ENERGY: "Energy",
  EXERCISE: "Exercise",
  MEDICAL: "medical",
  MEDIADIGITALHABITS: "MediaDigitalHabits",
  DIETNUTRITION: "DietNutrition",
  CONFIDENTIAL_ADVISOR: "ConfidentialAdvisorTheme",
};

export const consultNotesTemplateFirstConsult =
  "-Request for help/goal: \n-Issues (complaints/opportunities): \n-Circumstances: \n-Personal style (name at least 1 strength): ";
export const consultNotesTemplateFollowUpConsult =
  "-Discussed during session: \n-Advice/tip/assignment: \n-Next steps agreed upon: \n-Other: ";

export const pageSize = 10;
export const smallTablePageSize = 5;
export const ENTER_KEY = "Enter";
export const BACKSPACE_KEY = "Backspace";

export const delayInMinutesBetweenSessionTables = 120;
export const defaultSessionDurationInMinutes = 30;

export const PROFILE_VALUES = {
  PROFILE: "profile",
  LOGOUT: "logout",
};
export const timeSnackbar = 2000;

export const QUESTIONS_KEYS = {
  CONFIDENCE: "self-confidence",
  STRESS: "stress-and-worry",
  WORK: "work-performance",
  PURPOSE: "meaning",
  RELATIONSHIPS: "social-relationships",
  HEALTH: "lifestyle",
  SLEEP: "sleep",
  MINDFULNESS: "mindfulness",
  MEDICAL: "medical",
};

export const HELP_NEEDED_VALUES = {
  TRUE: "true",
  NO_REFER_TO_IPRACTICE: "noReferToiPractice", // legacy
  REFER_TO_OPENUP_NETWORK: "referToOpenUpNetwork",
  NO_REFER_TO_OTHER_PROVIDER: "noReferToOtherProvider",
  NO_CLIENT_AT_RISK: "noClientAtRisk", // legacy
  OTHER: "other",
};

export const NETWORK_PROVIDER_VALUES = {
  PSYNED: "psyNed",
  IPRACTICE: "iPractice",
  KIRINUS_HEALTH: "kirinusHealth",
  ONEBRIGHT: "oneBright",
};

export const PROVIDER_VALUES = {
  OTHER: "other",
  DOCTOR: "doctor",
};

export const NEXT_STEPS = {
  WEEKS: "weeks",
  MONTHS: "months",
  BREAK: "break",
};

export const ConsultActions = {
  JOIN: "join",
  RESCHEDULE: "reschedule",
  CANCEL: "cancel",
};
export const APPLICATION_INSIGHTS_APP_NAME = "OpenUp.Admin";

export const FEATURE_FLAGS = {
  AI_TRANSCRIPTION_FEATURE: "TranscriptionFeature",
} as const;

export const ConsultReviewQuestionsKeys = {
  Question1: "Consult.Review.Question1",
  Question2: "Consult.Review.Question2",
  Question3: "Consult.Review.Question3",
  Question4: "Consult.Review.Feedback",
};

// Financial-wellbeing consults store review answers under these dedicated
// keys; anything rendering feedback must match both this set and the
// generic ConsultReviewQuestionsKeys above.
export const FinancialWellbeingConsultReviewQuestionsKeys = {
  Question1: "Consult.Review.FinancialWellbeing.Question1",
  Question2: "Consult.Review.FinancialWellbeing.Question2",
  Question3: "Consult.Review.FinancialWellbeing.Question3",
  Question4: "Consult.Review.FinancialWellbeing.Feedback",
};
